// src/components/profile/QuickJumpBar.jsx - WITH MEETING MODE FILTER
// ============================================================
// CLIENT ID: Accepts clientId as STRING prop, no hardcoded default
// FIX: Changed from numeric 1 to null - parent MUST pass string clientId
// ============================================================
import React, { useContext } from 'react';
import { DollarSign, Settings, Target, AlertTriangle, Calendar, CheckCircle, FileText, ListTodo } from 'lucide-react';
import ShareMenu from './ShareMenu';
import { MeetingModeContext } from '../../contexts/MeetingModeContext';

/**
 * Quick Jump bar for fast section switching in single-section display mode
 * Props:
 * - activeSection: string - Currently active section ID
 * - onSectionChange: (sectionId: string) => void - Section change handler
 * - useCaseId: string - Current use case ID for share functionality
 * - clientId: string - Current client ID (MUST be string for BigQuery compatibility)
 * - lens: string - Current lens (quickwins|70_30|50_50)
 * - displayMode: string - Current display mode (raw|calibrated)
 */

const sectionConfig = [
  {
    id: 'overview',
    label: 'Overview',
    shortLabel: 'Overview',
    icon: FileText,
    color: 'text-[#18365E]',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    activeColor: 'bg-[#18365E] text-white'
  },
  {
    id: 'roi-details',
    label: 'ROI Details',
    shortLabel: 'ROI',
    icon: DollarSign,
    color: 'text-green-600',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200',
    activeColor: 'bg-green-600 text-white'
  },
  {
    id: 'feasibility',
    label: 'Feasibility',
    shortLabel: 'Feasibility',
    icon: Settings,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    activeColor: 'bg-blue-600 text-white'
  },
  {
    id: 'strategic-alignment',
    label: 'Strategic Alignment',
    shortLabel: 'Alignment',
    icon: Target,
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-200',
    activeColor: 'bg-purple-600 text-white'
  },
  {
    id: 'risks-governance',
    label: 'Risks & Governance',
    shortLabel: 'Risks',
    icon: AlertTriangle,
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
    activeColor: 'bg-amber-600 text-white'
  },
  {
    id: 'roadmap',
    label: 'Roadmap & Timeline',
    shortLabel: 'Roadmap',
    icon: Calendar,
    color: 'text-indigo-600',
    bgColor: 'bg-indigo-50',
    borderColor: 'border-indigo-200',
    activeColor: 'bg-indigo-600 text-white'
  },
  {
    id: 'actions',
    label: 'Actions',
    shortLabel: 'Actions',
    icon: ListTodo,
    color: 'text-orange-600',
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-200',
    activeColor: 'bg-orange-600 text-white'
  },
  {
    id: 'recommendation',
    label: 'Recommendation',
    shortLabel: 'Recommendation',
    icon: CheckCircle,
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-200',
    activeColor: 'bg-emerald-600 text-white'
  }
];

// Meeting Mode minimal tabs - only show essential sections
const MEETING_MODE_TABS = ['overview', 'roi-details', 'roadmap', 'actions'];

export default function QuickJumpBar({ 
  activeSection, 
  onSectionChange,
  useCaseId,
  // ============================================================
  // FIX: Changed from numeric 1 to null
  // Parent component MUST pass clientId as STRING from AuthContext
  // ============================================================
  clientId = null,
  lens = '70_30',
  displayMode = 'raw'
}) {
  // Get meeting mode state from context
  const { meetingMode } = useContext(MeetingModeContext);
  
  // Filter sections based on meeting mode
  const visibleSections = meetingMode 
    ? sectionConfig.filter(section => MEETING_MODE_TABS.includes(section.id))
    : sectionConfig;

  return (
    <div className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-30">
      <div className="max-w-7xl mx-auto px-6 py-3">
        <div className="flex items-center justify-between">
          {/* Left side - Quick Jump navigation */}
          <div className="flex items-center">
            <h2 className="text-sm font-semibold text-[#18365E] mr-3">Quick Jump:</h2>
            <div className="flex items-center gap-2">
              {visibleSections.map((section) => {
                const Icon = section.icon;
                const isActive = activeSection === section.id;
                
                return (
                  <button
                    key={section.id}
                    onClick={() => onSectionChange(section.id)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#18365E] focus:ring-offset-1 ${
                      isActive
                        ? section.activeColor + ' shadow-sm'
                        : `${section.color} ${section.bgColor} ${section.borderColor} border hover:shadow-sm hover:scale-105`
                    }`}
                    aria-current={isActive ? 'page' : undefined}
                    title={section.label}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="hidden sm:inline">{section.shortLabel}</span>
                  </button>
                );
              })}
            </div>
          </div>
          
          {/* Right side - Share menu */}
          {useCaseId && clientId && (
            <ShareMenu 
              useCaseId={useCaseId}
              clientId={clientId}
              lens={lens}
              displayMode={displayMode}
            />
          )}
        </div>
      </div>
    </div>
  );
}